import { TextField, RadioGroup, FormControlLabel, Radio, FormGroup, Checkbox } from '@mui/material';
import InputWrapper from '../../login/forms/InputWrapper';
import { isTextField, isNumberField, isDateField, isRadioField, isCheckboxField, isEmailField } from '../types/guards';
import { fields } from '../constants/fields';
import type { Action, BaseField, Field, FieldProps, Fields, State } from '../types';
import Debug from '../components/Debug';
import MultiInputWrapper from '../components/MultiInputWrapper';
import FieldErrors from '../components/FieldErrors';
import FieldDescription from '../components/FieldDescription';

const getErrors = (field: BaseField) => (field.errors ? field.errors : undefined);

const EnrollForm = ({
  step,
  state,
  dispatch,
  handleChangeStep,
}: {
  step: number;
  state: State;
  dispatch: React.Dispatch<Action>;
  handleChangeStep: (step: number, command: string) => void;
}) => {
  const stepFields: Fields = fields?.[step];
  const urlParams = new URLSearchParams(window.location.search);
  const hasDebug = urlParams.get('debug') === 'true';

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLLabelElement | HTMLDivElement>) => {
    if (e.key === 'Enter' && e.shiftKey) {
      handleChangeStep(step, 'prev');
    } else if (e.key === 'Enter') {
      handleChangeStep(step, 'next');
    }
  };

  return (
    <>
      {hasDebug && <Debug state={state} />}
      <form>
        <p>I campi obbligatori sono contrassegnati da un asterisco (*)</p>
        <div className="flex flex-wrap justify-between">
          {stepFields.map((field: Field) => {
            const errors = state?.[field.name]?.errors;
            const hasError = state.hasErrors === step && errors?.length > 0;
            const transformFn = field?.transformFn ? field.transformFn : (x: string) => x;
            return (
              <div key={field.name} className="p-0 w-full xl:w-6/12 xl:odd:pr-2 xl:even:pl-2">
                <fieldset className="my-0.5 rounded-md font-semibold">
                  <legend>
                    {getErrors(field)?.includes('required') ? ' *' : ''}
                    {field.name}
                    {!!field?.unit && field?.unit !== '' ? ` (${field.unit})` : ''}
                    {isNumberField(field) && field?.unit === '' ? ` (numero)` : ''}:
                  </legend>
                  {(isTextField(field) || isNumberField(field) || isDateField(field) || isEmailField(field)) && (
                    <InputWrapper>
                      <>
                        <TextField
                          type={field.type}
                          value={(state[field.name].value as string) || ''}
                          error={hasError}
                          size="small"
                          fullWidth
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            dispatch({
                              type: `set_${field.name}`,
                              value: { fieldName: '', fieldValue: transformFn(e.target.value) },
                            });
                          }}
                          onKeyDown={onKeyDown}
                        />
                        {field?.note && field?.note !== '' && (
                          <span dangerouslySetInnerHTML={{ __html: field.note }}></span>
                        )}
                        <FieldErrors hasErrors={state.hasErrors} errors={errors} />
                        {isDateField(field) && (
                          <span> {state?.nextAvailableDaysList?.join(', ').replace(/,\s*$/, '')}</span>
                        )}
                      </>
                    </InputWrapper>
                  )}
                  {isRadioField(field) && (
                    <>
                      <FieldDescription description={field?.description || ''} />
                      <MultiInputWrapper error={hasError}>
                        <>
                          <RadioGroup name={field.name}>
                            {field?.options?.map((radio, i) => (
                              <FormControlLabel
                                key={`${radio.value}-${radio.name}-${i}`}
                                value={radio.value}
                                control={<Radio />}
                                label={radio.name}
                                checked={radio.value === state[field.name].value}
                                onChange={() => {
                                  dispatch({
                                    type: `set_${field.name}`,
                                    value: {
                                      fieldName: radio.name,
                                      fieldValue: radio.value,
                                    },
                                  });
                                }}
                                onKeyDown={onKeyDown}
                              />
                            ))}
                            <FieldErrors hasErrors={state.hasErrors} errors={errors} />
                          </RadioGroup>
                          {field?.note && field?.note !== '' && (
                            <div dangerouslySetInnerHTML={{ __html: field.note }}></div>
                          )}
                        </>
                      </MultiInputWrapper>
                    </>
                  )}
                  {isCheckboxField(field) && (
                    <>
                      <FieldDescription description={field?.description || ''} />
                      <MultiInputWrapper error={hasError}>
                        <>
                          <FormGroup>
                            {field?.options?.map((checkbox) => (
                              <FormControlLabel
                                key={checkbox.value}
                                name={field.name}
                                value={checkbox.value}
                                checked={
                                  !!state[field.name].value.find((el: FieldProps) => el.fieldValue === checkbox.value)
                                }
                                control={<Checkbox />}
                                label={checkbox.name}
                                onChange={() => {
                                  dispatch({
                                    type: `add_${field.name}`,
                                    value: { fieldName: checkbox.name, fieldValue: checkbox.value },
                                  });
                                }}
                                onKeyDown={onKeyDown}
                              />
                            ))}
                            <FieldErrors hasErrors={state.hasErrors} errors={errors} />
                          </FormGroup>
                          {field?.note && field?.note !== '' && (
                            <div dangerouslySetInnerHTML={{ __html: field.note }}></div>
                          )}
                        </>
                      </MultiInputWrapper>
                    </>
                  )}
                </fieldset>
              </div>
            );
          })}
        </div>
      </form>
    </>
  );
};

export default EnrollForm;
